import React, { useRef, useState, useEffect } from 'react';
import { motion, useInView } from 'framer-motion';
import tabletImg from '../assets/platform/tablet.png';

const PRIMARY = '#3498db';
const NAVY = '#0F172A';
const GREEN = '#10B981';

const features = [
  {
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
        <path d="M9 12l2 2 4-4M21 12a9 9 0 11-18 0 9 9 0 0118 0z" stroke={GREEN} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
    title: 'Create & Manage Shipments',
    desc: 'Book shipments and schedule pickups in seconds from one dashboard.',
    color: GREEN,
  },
  {
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
        <circle cx="12" cy="12" r="9" stroke={PRIMARY} strokeWidth="2" />
        <path d="M12 7v5l3 3" stroke={PRIMARY} strokeWidth="2" strokeLinecap="round" />
      </svg>
    ),
    title: 'Real-Time Tracking',
    desc: 'Live status updates for every consignment with SMS and WhatsApp alerts.',
    color: PRIMARY,
  },
  {
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
        <path d="M3 3h18v4H3zM3 10h18v4H3zM3 17h18v4H3z" stroke="#F59E0B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
    title: 'Insights & Analytics',
    desc: 'Understand your shipping patterns, costs and delivery performance.',
    color: '#F59E0B',
  },
  {
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
        <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" stroke="#8B5CF6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M14 2v6h6M16 13H8M16 17H8M10 9H8" stroke="#8B5CF6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
    title: 'Automatic E-BILTY',
    desc: 'Legally compliant digital LR generated instantly for every shipment.',
    color: '#8B5CF6',
  },
];

// Device showcase — tablet only
const DeviceShowcase: React.FC = () => (
  <div style={{
    borderRadius: 20,
    overflow: 'hidden',
    border: '3px solid #000',
    boxShadow: '0 40px 120px rgba(0,0,0,0.70), 0 12px 40px rgba(0,0,0,0.45), 0 0 0 6px #000',
    background: '#000',
    width: '100%',
  }}>
    <img
      src={tabletImg}
      alt="Instaship tablet dashboard"
      style={{ display: 'block', width: '100%', height: 'auto' }}
    />
  </div>
);

const PlatformShowcase: React.FC = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 940);
  const [isNarrow, setIsNarrow] = useState(window.innerWidth >= 940 && window.innerWidth < 1150);
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });

  useEffect(() => {
    const handleResize = () => {
      const w = window.innerWidth;
      setIsMobile(w < 940);
      setIsNarrow(w >= 940 && w < 1150);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <section ref={ref} style={{ background: 'linear-gradient(135deg, #0F172A 0%, #1e3a5f 60%, #1a6fa8 100%)', padding: isMobile ? '60px 0' : '90px 0' }}>
      <div style={{ display: 'flex', flexDirection: (isMobile || isNarrow) ? 'column' : 'row', alignItems: 'center', gap: (isMobile || isNarrow) ? 48 : 56 }}>

        {/* Left: text */}
        <div style={{ flex: 1, minWidth: 0, paddingLeft: isMobile ? 24 : (isNarrow ? 32 : 64), paddingRight: isMobile ? 24 : (isNarrow ? 32 : 40) }}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
          >
            <h2 style={{ fontSize: isMobile ? '2.09rem' : '2.86rem', fontWeight: 900, color: '#fff', lineHeight: 1.15, letterSpacing: '-0.02em', marginBottom: 16 }}>
              One Powerful Platform.<br />
              <span style={{ color: PRIMARY }}>Complete Shipment Control.</span>
            </h2>
            <p style={{ fontSize: 17.6, color: '#b0bfd0', marginBottom: 36, lineHeight: 1.7, maxWidth: 440 }}>
              Manage your entire logistics operation from a single, intuitive dashboard. Built for Indian B2B businesses.
            </p>
          </motion.div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            {features.map((f, i) => (
              <motion.div
                key={f.title}
                initial={{ opacity: 0, x: -20 }}
                animate={inView ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.5, delay: 0.2 + i * 0.1 }}
                style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}
              >
                <div style={{ width: 36, height: 36, borderRadius: 10, background: `${f.color}15`, border: `1px solid ${f.color}30`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  {f.icon}
                </div>
                <div>
                  <div style={{ fontSize: 15.4, fontWeight: 700, color: '#e2e8f0', marginBottom: 3 }}>{f.title}</div>
                  <div style={{ fontSize: 14.3, color: '#b0bfd0', lineHeight: 1.5 }}>{f.desc}</div>
                </div>
              </motion.div>
            ))}
          </div>


        </div>

        {/* Right: device screenshots */}
        {(!isMobile && !isNarrow) && (
          <div style={{
            width: 1, alignSelf: 'stretch', flexShrink: 0,
            background: 'linear-gradient(to bottom, transparent, rgba(255,255,255,0.1) 30%, rgba(255,255,255,0.1) 70%, transparent)',
          }} />
        )}

        {/* Right: device screenshots */}
        <motion.div
          style={{
            flex: (isMobile || isNarrow) ? '1 1 auto' : '0 0 55%',
            minWidth: 0,
            width: (isMobile || isNarrow) ? '100%' : undefined,
            maxWidth: (isMobile || isNarrow) ? 700 : undefined,
            margin: (isMobile || isNarrow) ? '0 auto' : undefined,
            paddingLeft: isMobile ? 24 : (isNarrow ? 32 : 0),
            paddingRight: isMobile ? 24 : (isNarrow ? 32 : 48),
          }}
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7, delay: 0.3 }}
        >
          <DeviceShowcase />
        </motion.div>
      </div>
    </section>
  );
};

export default PlatformShowcase;
